import Link from 'next/link'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useRouter} from 'next/router'
import {clsx} from 'clsx'
import {addLocalePrefix} from '@/utilities/add-locale-prefix'
import type {Block} from '@/services/kontent-ai/types'
import {CardImage} from '@/products/flexible-web-toolkit/components/card/card-image'
import {CardFooter} from '@/products/flexible-web-toolkit/components/card/card-footer'
import {Card} from '@/products/flexible-web-toolkit/components/card'
import {ReusableCTAButtonComponent} from '../cta-button'
import type {CtaButtonContentItem} from '../cta-button'

export type EnhancedCardContentItem = IContentItem<{
	title: Elements.TextElement
	image: Elements.AssetsElement
	imageOnLeft: Elements.MultipleChoiceElement
	content: Elements.RichTextElement
	cardContentInCenter: Elements.MultipleChoiceElement
	button: Elements.LinkedItemsElement<CtaButtonContentItem>
	stackButtons: Elements.MultipleChoiceElement
	hasTitleLink: Elements.MultipleChoiceElement
	titleUrl: Elements.TextElement
	makeTheImageAClickableLink: Elements.MultipleChoiceElement
	imageClickDestinationUrl: Elements.TextElement
	removeBorders: Elements.MultipleChoiceElement
	openInNewTab: Elements.MultipleChoiceElement
}>

export const EnhancedCardBlock: Block<EnhancedCardContentItem> = ({
	block: {
		elements: {
			button,
			content,
			hasTitleLink,
			image,
			imageOnLeft,
			makeTheImageAClickableLink,
			openInNewTab,
			removeBorders,
			stackButtons,
			title,
			titleUrl,
			cardContentInCenter,
			imageClickDestinationUrl,
		},
		system: {codename},
	},
	...context
}) => {
	const {locale = ''} = useRouter()
	let clickableImageUrl = ''
	if (makeTheImageAClickableLink[0]?.codename === 'yes') {
		const buttonLink = button[0]?.elements.url
		if (imageClickDestinationUrl !== '') {
			clickableImageUrl = addLocalePrefix(
				imageClickDestinationUrl,
				locale
			)
		} else if (buttonLink) {
			clickableImageUrl = addLocalePrefix(buttonLink, locale)
		}
	}
	return (
		<Card
			className={clsx(
				removeBorders[0]?.codename === 'yes' ? undefined : 'p-4',
				`h-full flex-none text-black`
			)}
			data-kontent-element-codename="enhanced_card"
			imageOnLeft={imageOnLeft[0]?.codename === 'yes'}
			removeBorders={removeBorders[0]?.codename === 'yes'}
			cardContentInCenter={cardContentInCenter[0]?.codename === 'yes'}
			title={
				<span data-kontent-element-codename="card_title">
					{hasTitleLink[0]?.codename === 'yes' && titleUrl !== '' ? (
						<Link
							className="pb-2 underline"
							href={titleUrl || ''}
							rel="noreferrer"
							target={
								openInNewTab[0]?.codename === 'yes'
									? '_blank'
									: '_self'
							}
						>
							{}

							{title}
						</Link>
					) : (
						title
					)}
				</span>
			}
		>
			{image[0] ? (
				<CardImage
					alt={image[0].description}
					className={clsx(
						'h-48',
						removeBorders[0]?.codename === 'yes'
							? 'object-cover'
							: '',
						codename.startsWith('brand')
							? 'object-cover'
							: 'object-contain'
					)}
					data-kontent-element-codename="image"
					imageUrl={clickableImageUrl}
					src={image[0].url}
				/>
			) : null}
			<div
				className="content m-0 w-full break-words"
				dangerouslySetInnerHTML={{__html: content.value}}
				data-kontent-element-codename="text"
			/>
			<CardFooter>
				<>
					{button.map((b, index, arr) => (
						<ReusableCTAButtonComponent
							block={b}
							className={clsx(
								`mb-2`,
								arr.length === 1 ? 'w-full' : null,
								index < arr.length - 1 && arr.length !== 1
									? 'mr-4'
									: null,
								removeBorders[0]?.codename === 'yes' ||
									stackButtons[0]?.codename === 'yes'
									? 'w-full'
									: null
							)}
							key={b.system.id}
							{...context}
						/>
					))}
				</>
			</CardFooter>
		</Card>
	)
}
